class VideoCarousel extends HTMLElement {
  constructor() {
    super();
    this.track = this.querySelector('[id^="VideoCarouselTrack"]');
    this.prevBtn = this.querySelector('[data-prev]');
    this.nextBtn = this.querySelector('[data-next]');

    // Slides en lecture au clic
    this.querySelectorAll('.video-carousel__media[data-autoplay="false"] .video-carousel__play').forEach((btn) => {
      btn.addEventListener('click', () => this.playSlide(btn));
    });

    // Slides en lecture automatique
    const autoplayMedia = this.querySelectorAll('.video-carousel__media[data-autoplay="true"]');
    if (autoplayMedia.length) {
      this.setupAutoplayObserver(autoplayMedia);
    }

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.scrollByAmount(-1));
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.scrollByAmount(1));
    }

    if (this.track) {
      this.track.addEventListener('scroll', () => this.updateArrows(), { passive: true });
      this.updateArrows();
    }
  }

  scrollByAmount(direction) {
    if (!this.track) return;
    const slide = this.track.querySelector('.video-carousel__slide');
    const slideWidth = slide ? slide.getBoundingClientRect().width : this.track.clientWidth;
    const gap = parseFloat(getComputedStyle(this.track).gap || 16);
    this.track.scrollBy({ left: direction * (slideWidth + gap), behavior: 'smooth' });
  }

  updateArrows() {
    if (!this.track || !this.prevBtn || !this.nextBtn) return;
    const maxScroll = this.track.scrollWidth - this.track.clientWidth - 2;
    this.prevBtn.disabled = this.track.scrollLeft <= 0;
    this.nextBtn.disabled = this.track.scrollLeft >= maxScroll;
  }

  /* ---------- Lecture au clic ---------- */

  playSlide(btn) {
    const media = btn.closest('.video-carousel__media');
    if (!media) return;

    // Coupe toute autre vidéo native en cours de lecture
    this.querySelectorAll('.video-carousel__video').forEach((v) => v.pause());

    this.reveal(media);

    const nativeVideo = media.querySelector('video');
    if (nativeVideo) {
      nativeVideo.play();
      return;
    }

    const external = media.querySelector('[data-external-video]');
    if (external && !external.dataset.loaded) {
      // Lecture déclenchée par un clic utilisateur : pas besoin du mute pour l'autoplay YouTube
      this.loadExternal(external, { autoplay: true, muted: false, loop: false });
    }
  }

  /* ---------- Lecture automatique (au scroll) ---------- */

  setupAutoplayObserver(mediaList) {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.startAutoplay(entry.target);
          } else {
            this.stopAutoplay(entry.target);
          }
        });
      },
      { threshold: 0.6 }
    );

    mediaList.forEach((media) => this.observer.observe(media));
  }

  startAutoplay(media) {
    this.reveal(media);

    const nativeVideo = media.querySelector('video');
    if (nativeVideo) {
      nativeVideo.muted = true;
      nativeVideo.play().catch(() => {});
      return;
    }

    const external = media.querySelector('[data-external-video]');
    if (external && !external.dataset.loaded) {
      this.loadExternal(external, { autoplay: true, muted: true, loop: true });
    }
  }

  stopAutoplay(media) {
    const nativeVideo = media.querySelector('video');
    if (nativeVideo) {
      nativeVideo.pause();
      return;
    }

    // Vidéos externes : on retire l'iframe pour couper réellement la lecture
    // (pas de contrôle postMessage fiable cross-plateforme) ; elle est
    // rechargée à la prochaine entrée dans le viewport.
    const external = media.querySelector('[data-external-video]');
    if (external && external.dataset.loaded) {
      external.innerHTML = '';
      delete external.dataset.loaded;
    }
  }

  /* ---------- Commun ---------- */

  reveal(media) {
    const poster = media.querySelector('[data-poster]');
    const player = media.querySelector('[data-player]');
    if (poster) poster.hidden = true;
    if (player) player.hidden = false;
  }

  loadExternal(external, { autoplay, muted, loop }) {
    const base = external.dataset.embedBase;
    const id = external.dataset.videoId;
    if (!base) return;

    let src = `${base}?rel=0`;
    if (autoplay) src += '&autoplay=1';
    if (muted) src += '&mute=1';
    if (loop) src += `&loop=1&playlist=${id}`;

    const iframe = document.createElement('iframe');
    iframe.src = src;
    iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share');
    iframe.setAttribute('allowfullscreen', '');
    iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
    external.appendChild(iframe);
    external.dataset.loaded = 'true';
  }
}

customElements.define('video-carousel', VideoCarousel);